import {useNavigate} from 'react-router-dom';

import {useActiveCasusIdOrThrow} from '_/hooks/useActiveCasusId';
import {Arguments, getRouteUrl} from '_/utilities';
import {Optional} from '_/utilities/types';

type ExtractModuleArguments<M extends Arguments['module']> = Omit<
  Extract<Arguments, {module: M}>,
  'module'
>;

type CasusLogViewParams = Optional<
  ExtractModuleArguments<'casus-logView'>,
  'casusId'
>;
type CasusDocumentsParams = Optional<
  ExtractModuleArguments<'casus-documents'>,
  'casusId'
>;
type CasusJustitiaSubmissionParams = {
  casusId?: string;
  submissionId: string;
  reloadSubmissionFolders?: boolean;
};
type CasusEmailsParams = Optional<
  ExtractModuleArguments<'casus-communication'>,
  'casusId'
>;
type CasusNotesParams = Optional<
  ExtractModuleArguments<'casus-notes'>,
  'casusId'
>;
type CasusTasksParams = Optional<
  ExtractModuleArguments<'tasks-management'>,
  'casusId'
>;
type CasusFeesParams = Optional<
  ExtractModuleArguments<'casus-fees'>,
  'casusId'
>;
type CasusInvoiceContainersParams = Optional<
  ExtractModuleArguments<'casus-invoice-containers'>,
  'casusId'
>;
type CasusInvoicesParams = Optional<
  ExtractModuleArguments<'casus-invoices'>,
  'casusId'
>;
type CasusPaymentsParams = Optional<
  ExtractModuleArguments<'casus-payments'>,
  'casusId'
>;
type CasusClientMoneyParams = Optional<
  ExtractModuleArguments<'casus-client-money'>,
  'casusId'
>;

type ContactsParams = ExtractModuleArguments<'contacts'>;

type CommunicationsParams = Omit<
  ExtractModuleArguments<'communication'>,
  'view'
>;

type ExtractInvoicingViewArguments<
  V extends ExtractModuleArguments<'invoicing'>['view'],
> = Omit<Extract<Arguments, {module: 'invoicing'; view: V}>, 'module' | 'view'>;

type InvoiceContainersParams =
  ExtractInvoicingViewArguments<'invoice-containers'>;
type InvoicesParams = ExtractInvoicingViewArguments<'invoices'>;
type PaymentsParams = ExtractInvoicingViewArguments<'payments'>;
type ClientMoneyParams = ExtractInvoicingViewArguments<'client-money'>;
type FinanceExportParams = ExtractInvoicingViewArguments<'finance-export'>;

type SearchParams = ExtractModuleArguments<'search'>;

type ReturnType = {
  goToCasusLogView: (params: CasusLogViewParams) => void;
  goToCasusDocuments: (params: CasusDocumentsParams) => void;
  goToCasusJustitiaSubmission: (params: CasusJustitiaSubmissionParams) => void;
  goToCasusEmails: (params: CasusEmailsParams) => void;
  goToCasusNotes: (params: CasusNotesParams) => void;
  goToCasusTasks: (params: CasusTasksParams) => void;
  goToCasusFees: (params: CasusFeesParams) => void;
  goToCasusInvoiceContainers: (params: CasusInvoiceContainersParams) => void;
  goToCasusInvoices: (params: CasusInvoicesParams) => void;
  goToCasusPayments: (params: CasusPaymentsParams) => void;
  goToCasusClientMoney: (params: CasusClientMoneyParams) => void;
  goToContacts: (params: ContactsParams) => void;
  goToTriage: (params: CommunicationsParams) => void;
  goToAllEmails: (params: CommunicationsParams) => void;
  goToInvoiceContainers: (params: InvoiceContainersParams) => void;
  goToInvoices: (params: InvoicesParams) => void;
  goToPayments: (params: PaymentsParams) => void;
  goToClientMoney: (params: ClientMoneyParams) => void;
  goToFinanceExport: (params: FinanceExportParams) => void;
  goToSearch: (params: SearchParams) => void;
};

export const useRouteNavigate = (): ReturnType => {
  const navigate = useNavigate();
  const {getActiveCasusIdOrThrow} = useActiveCasusIdOrThrow();

  function goToCasusLogView({
    casusId = getActiveCasusIdOrThrow(),
    clearFilters = true,
    ...params
  }: CasusLogViewParams) {
    navigate(
      getRouteUrl({module: 'casus-logView', casusId, clearFilters, ...params})
    );
  }

  function goToCasusDocuments({
    casusId = getActiveCasusIdOrThrow(),
    clearFilters = true,
    ...params
  }: CasusDocumentsParams) {
    navigate(
      getRouteUrl({module: 'casus-documents', casusId, clearFilters, ...params})
    );
  }

  function goToCasusJustitiaSubmission({
    submissionId,
    casusId = getActiveCasusIdOrThrow(),
    reloadSubmissionFolders,
  }: CasusJustitiaSubmissionParams) {
    navigate(
      getRouteUrl({
        module: 'casus-documents-submissions',
        casusId,
        submissionId,
        reloadSubmissionFolders,
      })
    );
  }

  function goToCasusEmails({
    casusId = getActiveCasusIdOrThrow(),
    clearFilters = true,
    ...params
  }: CasusEmailsParams) {
    navigate(
      getRouteUrl({
        module: 'casus-communication',
        casusId,
        clearFilters,
        ...params,
      })
    );
  }

  function goToCasusNotes({
    casusId = getActiveCasusIdOrThrow(),
    clearFilters = true,
    ...params
  }: CasusNotesParams) {
    navigate(
      getRouteUrl({module: 'casus-notes', casusId, clearFilters, ...params})
    );
  }

  function goToCasusTasks({
    casusId = getActiveCasusIdOrThrow(),
    clearFilters = true,
    ...params
  }: CasusTasksParams) {
    navigate(
      getRouteUrl({
        module: 'tasks-management',
        casusId,
        clearFilters,
        ...params,
      })
    );
  }

  function goToCasusFees({
    casusId = getActiveCasusIdOrThrow(),
    clearFilters = true,
    ...params
  }: CasusFeesParams) {
    navigate(
      getRouteUrl({module: 'casus-fees', casusId, clearFilters, ...params})
    );
  }

  function goToCasusInvoiceContainers({
    casusId = getActiveCasusIdOrThrow(),
    clearFilters = true,
    ...params
  }: CasusInvoiceContainersParams) {
    navigate(
      getRouteUrl({
        module: 'casus-invoice-containers',
        casusId,
        clearFilters,
        ...params,
      })
    );
  }

  function goToCasusInvoices({
    casusId = getActiveCasusIdOrThrow(),
    clearFilters = true,
    ...params
  }: CasusInvoicesParams) {
    navigate(
      getRouteUrl({module: 'casus-invoices', casusId, clearFilters, ...params})
    );
  }

  function goToCasusPayments({
    casusId = getActiveCasusIdOrThrow(),
    clearFilters = true,
    ...params
  }: CasusPaymentsParams) {
    navigate(
      getRouteUrl({module: 'casus-payments', casusId, clearFilters, ...params})
    );
  }

  function goToCasusClientMoney({
    casusId = getActiveCasusIdOrThrow(),
    clearFilters = true,
    ...params
  }: CasusClientMoneyParams) {
    navigate(
      getRouteUrl({
        module: 'casus-client-money',
        casusId,
        clearFilters,
        ...params,
      })
    );
  }

  function goToContacts({clearFilters = true, ...params}: ContactsParams) {
    navigate(getRouteUrl({module: 'contacts', clearFilters, ...params}));
  }

  function goToTriage({clearFilters = true, ...params}: CommunicationsParams) {
    navigate(
      getRouteUrl({
        module: 'communication',
        view: 'triage',
        clearFilters,
        ...params,
      })
    );
  }

  function goToAllEmails({
    clearFilters = true,
    ...params
  }: CommunicationsParams) {
    navigate(
      getRouteUrl({
        module: 'communication',
        view: 'all',
        clearFilters,
        ...params,
      })
    );
  }

  function goToInvoiceContainers({
    clearFilters = true,
    ...params
  }: InvoiceContainersParams) {
    navigate(
      getRouteUrl({
        module: 'invoicing',
        view: 'invoice-containers',
        clearFilters,
        ...params,
      })
    );
  }

  function goToInvoices({clearFilters = true, ...params}: InvoicesParams) {
    navigate(
      getRouteUrl({
        module: 'invoicing',
        view: 'invoices',
        clearFilters,
        ...params,
      })
    );
  }

  function goToPayments({clearFilters = true, ...params}: PaymentsParams) {
    navigate(
      getRouteUrl({
        module: 'invoicing',
        view: 'payments',
        clearFilters,
        ...params,
      })
    );
  }

  function goToClientMoney({
    clearFilters = true,
    ...params
  }: ClientMoneyParams) {
    navigate(
      getRouteUrl({
        module: 'invoicing',
        view: 'client-money',
        clearFilters,
        ...params,
      })
    );
  }

  function goToFinanceExport({
    clearFilters = true,
    ...params
  }: FinanceExportParams) {
    navigate(
      getRouteUrl({
        module: 'invoicing',
        view: 'finance-export',
        clearFilters,
        ...params,
      })
    );
  }

  function goToSearch({clearFilters = true, ...params}: SearchParams) {
    navigate(getRouteUrl({module: 'search', clearFilters, ...params}));
  }

  return {
    goToCasusLogView,
    goToCasusDocuments,
    goToCasusJustitiaSubmission,
    goToCasusEmails,
    goToCasusNotes,
    goToCasusTasks,
    goToCasusFees,
    goToCasusInvoiceContainers,
    goToCasusInvoices,
    goToCasusPayments,
    goToCasusClientMoney,
    goToContacts,
    goToTriage,
    goToAllEmails,
    goToInvoiceContainers,
    goToInvoices,
    goToPayments,
    goToClientMoney,
    goToFinanceExport,
    goToSearch,
  };
};
